import { useEffect, useState } from "react";
import { ArrowUpRight, ImageOff, Loader2, ScanLine } from "lucide-react";
import { Page, Panel, SearchInput, usePagination, Pagination, PhotoLightbox } from "../lib/ui";
import { api, humanDate, rowLink } from "../lib/api";
import { RuhsatCapture } from "./WorkPanel";

function RuhsatThumb({ wid }) {
  const [photo, setPhoto] = useState(null);
  const [failed, setFailed] = useState(false);
  const [full, setFull] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/ruhsat-archive/${wid}/photo`)
      .then((r) => { if (alive) setPhoto(r.data.photo); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [wid]);
  if (failed) return <span className="muted" title="Fotoğraf yüklenemedi"><ImageOff size={15} /></span>;
  if (!photo) return <span className="ruhsat-cell-thumb is-loading"><Loader2 size={13} className="spin" /></span>;
  const openFull = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFull(true);
  };
  return (
    <>
      <button type="button" className="photo-thumb-btn" title="Büyük görüntüle"
              data-testid={`ruhsat-photo-${wid}`} onClick={openFull}>
        <img src={photo} alt="Ruhsat" className="ruhsat-cell-thumb" />
      </button>
      {full && <PhotoLightbox src={photo} onClose={() => setFull(false)} />}
    </>
  );
}

export function RuhsatArchiveTable({ perPage = 15 }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [scanMsg, setScanMsg] = useState("");

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/ruhsat-archive", { params: q.trim() ? { q: q.trim() } : {} });
        setRows(data);
      } catch { /* yoksay */ }
      setLoading(false);
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q]);

  const pg = usePagination(rows, perPage);

  const handleScan = async (parsed) => {
    const plate = (parsed?.plate || "").trim();
    const chassis = (parsed?.chassis_no || "").trim();
    if (!plate && !chassis) {
      setScanMsg("Fotoğraftan plaka/şase okunamadı. El ile arayın.");
      return;
    }
    // 1) Önce plaka ile ara (eşleşirse direkt onu göster)
    if (plate) {
      try {
        const { data } = await api.get("/ruhsat-archive", { params: { q: plate } });
        if (data.length > 0) {
          setScanMsg(`Plakayla eşleşti: ${plate} (${data.length} kayıt)`);
          setQ(plate);
          return;
        }
      } catch { /* ignore */ }
    }
    // 2) Plaka yok veya eşleşmedi: şase ile dene (plaka değişmiş olabilir)
    if (chassis) {
      try {
        const { data } = await api.get("/ruhsat-archive", { params: { q: chassis } });
        if (data.length > 0) {
          const hint = plate
            ? `Plaka "${plate}" ile kayıt bulunamadı ama ŞASE "${chassis}" eşleşti → araç bu olabilir, plaka değişmiş olabilir (${data.length} kayıt).`
            : `Şase ile eşleşti: ${chassis} (${data.length} kayıt)`;
          setScanMsg(hint);
          setQ(chassis);
          return;
        }
      } catch { /* ignore */ }
    }
    setScanMsg(`Fotoğraftan okunan değer: ${plate || chassis} — arşivde kayıt bulunamadı.`);
    setQ(plate || chassis);
  };

  return (
    <div data-testid="ruhsat-archive-table">
      <div className="archive-toolbar">
        <SearchInput value={q} onChange={setQ} testid="ruhsat-archive-search"
                     placeholder="Plaka, şase no veya müşteri ara" />
      </div>
      <details className="ruhsat-scan-wrap" data-testid="ruhsat-scan-wrap">
        <summary data-testid="ruhsat-scan-toggle"><ScanLine size={14} /> Ruhsat fotoğrafı ile ara</summary>
        <div className="ruhsat-scan-inner">
          <RuhsatCapture onParsed={handleScan} />
          {scanMsg && <small className="muted" data-testid="ruhsat-scan-msg">{scanMsg}</small>}
        </div>
      </details>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Fotoğraf</th><th>Plaka</th><th>Şase No</th><th>Müşteri</th>
              <th>Araç</th><th>Personel</th><th>Tarih</th><th></th>
            </tr>
          </thead>
          <tbody>
            {pg.slice.length === 0 && (
              <tr><td colSpan="8" className="empty">
                {loading ? "Yükleniyor..." : q ? "Aramayla eşleşen ruhsat bulunamadı." : "Arşivde ruhsat fotoğrafı yok."}
              </td></tr>
            )}
            {pg.slice.map((w) => (
              <tr key={w.id} data-testid={`ruhsat-row-${w.id}`} className="clickable-row"
                  {...rowLink(`/is-fisi/${w.id}`)}>
                <td><RuhsatThumb wid={w.id} /></td>
                <td><strong>{w.plate || "—"}</strong></td>
                <td>{w.chassis_no || "—"}</td>
                <td>{w.customer_name || "—"}</td>
                <td>{[w.vehicle_make, w.vehicle_model, w.vehicle_year].filter(Boolean).join(" ") || "—"}</td>
                <td>{w.created_by_name || "—"}</td>
                <td>{humanDate(w.created_at)}</td>
                <td className="row-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="icon-button" title="İş fişini yeni sekmede aç"
                          data-testid={`ruhsat-open-${w.id}`}
                          {...rowLink(`/is-fisi/${w.id}`)}>
                    <ArrowUpRight size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total}
                    onChange={pg.setPage} testid="ruhsat-archive-pagination" />
      </div>
    </div>
  );
}

export default function RuhsatArchive() {
  return (
    <>
      <Page
        eyebrow="İŞ TAKİP"
        title="Ruhsat Arşivi"
        subtitle="İş fişleriyle saklanan araç ruhsatları. Plaka, şase no veya müşteri adıyla arayın; fotoğrafa tıklayınca büyük hali açılır."
      />
      <Panel className="module-panel">
        <RuhsatArchiveTable />
      </Panel>
    </>
  );
}
